import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import KNDetailsPopover from 'components/KNDetailsPopover/KNDetailsPopover'
import KNDetailsPopoverList from 'components/KNDetailsPopover/KNDetailsPopoverList'
import usePopoverState from 'components/KNDetailsPopover/usePopoverState'
import { KNDangerousGoodsIcon } from 'components/KNIcon/KNMaterialIcon'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { formatDecimals } from 'global/helpers/numberFormatters'
import { getFormattedUnit } from 'global/helpers/units'
import React, { type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import ProReference from 'screens/TripDetails/ProReference'
import Temperature from 'screens/TripDetails/Temperature'
import type { LegData } from 'screens/TripDetails/TripDetails.types'

export interface LegHeaderProps {
  trip: TripData
  leg: LegData
  weblinkToken?: string
  size?: 'large'
  stacked?: boolean
}

const LegHeader = ({
  trip,
  leg,
  weblinkToken,
  size,
  stacked,
}: LegHeaderProps): ReactElement => {
  const { t } = useTranslation()
  const pupStopReferences= leg.wayPoints.find((stop) => stop.type === 'PUP')?.references?.filter((reference) => ['HSN', 'HPO'].includes(reference.code)) ?? []
  const delStopReferences= leg.wayPoints.find((stop) => stop.type === 'DEL')?.references?.filter((reference) => ['HSN', 'HPO'].includes(reference.code)) ?? []
  const hasGeneralDetails = leg.attributes.customerReferences.length > 0
    || leg.attributes.pupReferences.length > 0
    || leg.attributes.delReferences.length > 0
    || !!leg.collectionInstructions
    || !!leg.deliveryInstructions
    || pupStopReferences.length > 0
    || delStopReferences.length > 0
  const hasCargoItemDetails = leg.attributes.cargoItemDetails.length > 0
  const hasShipmentDetails = hasGeneralDetails || hasCargoItemDetails
  const {
    anchorEl: shipmentDetailsAnchorEl,
    open: shipmentDetailsOpen,
    handleClick: handleShipmentDetailsClick,
    handleClose: handleShipmentDetailsClose,
  } = usePopoverState()
  const {
    anchorEl: dangerousDetailsAnchorEl,
    open: dangerousDetailsOpen,
    handleClick: handleDangerousDetailsClick,
    handleClose: handleDangerousDetailsClose,
  } = usePopoverState()

  const handleShipmentDetailsClickWrapper = useCallback((event: React.MouseEvent<HTMLElement>): void => {
    handleShipmentDetailsClick(event)
    analyticsEvent('polestar_cs_leg_details_popover', [getRouteName(location.pathname)])
  }, [])

  const handleDangerousDetailsClickWrapper = useCallback((event: React.MouseEvent<HTMLElement>): void => {
    handleDangerousDetailsClick(event)
    analyticsEvent('polestar_cs_leg_dangerous_goods_popover', [getRouteName(location.pathname)])
  }, [])

  const hasProReference = (leg: LegData) => {
    return leg.attributes.shipmentReferences?.some(item => item.code === 'AAK')
  }

  return (
    <>
      <Stack
        data-guide="leg-summary"
        spacing={1}
        direction={stacked ? 'column' : 'row'}
        justifyContent="space-between"
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography color="primary.main" variant={size === 'large' ? 'p2' : 'p4'}>
            {leg.shipmentNumber}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            {hasShipmentDetails && (
              <IconButton
                onClick={handleShipmentDetailsClickWrapper}
                size="small"
                color="primary"
                sx={{ padding: 0 }}
              >
                <InfoOutlinedIcon data-test="info-icon" />
              </IconButton>
            )}
            {leg.attributes.dangerousGoodsDetails.length > 0 && (
              <IconButton
                onClick={handleDangerousDetailsClickWrapper}
                size="small"
                color="error"
                sx={{ padding: 0 }}
              >
                <KNDangerousGoodsIcon />
              </IconButton>
            )}
            {leg.pharmaExtendedInformation && <Temperature trip={trip} shipmentNumber={leg.shipmentNumber} weblinkToken={weblinkToken} />}
          </Stack>
        </Stack>
        {hasProReference(leg) && <ProReference trip={trip} leg={leg} weblinkToken={weblinkToken}></ProReference>}
      </Stack>

      {hasShipmentDetails && (
        <KNDetailsPopover
          open={shipmentDetailsOpen}
          onClose={handleShipmentDetailsClose}
          anchorEl={shipmentDetailsAnchorEl}
        >
          {hasGeneralDetails && (
            <KNDetailsPopoverList
              items={[
                ...(leg.attributes.customerReferences.length > 0
                  ? [{
                      label: t('screens.cs.trip_details.shipment_details.references'),
                      value: leg.attributes.customerReferences,
                      stacked: true,
                    }]
                  : []),
                ...(leg.collectionInstructions
                  ? [{
                      label: t('screens.cs.trip_details.shipment_details.collection_instructions'),
                      value: leg.collectionInstructions,
                      stacked: true,
                    }]
                  : []),
                ...(leg.deliveryInstructions
                  ? [{
                      label: t('screens.cs.trip_details.shipment_details.delivery_instructions'),
                      value: leg.deliveryInstructions,
                      stacked: true,
                    }]
                  : []),
              ]}
            />
          )}
          {(leg.attributes.pupReferences.length > 0 || pupStopReferences.length > 0) && (
            <KNDetailsPopoverList
              description={t('screens.cs.trip_details.shipment_details.pickup_references')}
              items={[
                ...pupStopReferences.map((reference) => ({
                  label: t(`screens.cs.trip_details.shipment_details.${reference.code}`),
                  value: reference.value,
                })),
                ...leg.attributes.pupReferences.map((reference) => ({
                  value: reference,
                })),
              ]}
            />
          )}
          {(leg.attributes.delReferences.length > 0 || delStopReferences.length > 0) && (
            <KNDetailsPopoverList
              description={t('screens.cs.trip_details.shipment_details.delivery_references')}
              items={[
                ...delStopReferences.map((reference) => ({
                  label: t(`screens.cs.trip_details.shipment_details.${reference.code}`),
                  value: reference.value,
                })),
                ...leg.attributes.delReferences.map((reference) => ({
                  value: reference,
                })),
              ]}
            />
          )}
          {leg.attributes.cargoItemDetails.map((details) => (
            <KNDetailsPopoverList
              key={details.description}
              description={details.description}
              items={[
                {
                  label: t('screens.cs.trip_details.shipment_details.packages'),
                  value: details.packagesType
                    ? `${details.numberOfPackages} × ${details.packagesType}`
                    : details.numberOfPackages,
                },
                ...(details.innerPackage
                  ? [{
                      label: t('screens.cs.trip_details.shipment_details.inner_packages'),
                      value: details.innerPackageType
                        ? `${details.innerPackage} × ${details.innerPackageType}`
                        : details.innerPackage,
                    }]
                  : []),
                ...(details.stackable
                  ? [{
                      label: t('screens.cs.trip_details.shipment_details.stackable'),
                    }]
                  : []),
                ...(details.marksAndNumbers
                  ? [{
                      label: t('screens.cs.trip_details.shipment_details.marks_numbers'),
                      value: details.marksAndNumbers,
                    }]
                  : []),
                ...(details.weight
                  ? [{
                      label: t('screens.cs.trip_details.shipment_details.weight'),
                      value: `${formatDecimals(details.weight)} ${getFormattedUnit(details.weightUnit)}`,
                    }]
                  : []),
                ...(details.volume
                  ? [{
                      label: t('screens.cs.trip_details.shipment_details.volume'),
                      value: `${formatDecimals(details.volume)} ${getFormattedUnit(details.volumeUnit)}`,
                    }]
                  : []),
                ...(details.size.length && details.size.width && details.size.height
                  ? [{
                      label: t('screens.cs.trip_details.shipment_details.dimensions'),
                      value: `${formatDecimals(details.size.length)} / ${formatDecimals(details.size.width)} / ${formatDecimals(details.size.height)} ${getFormattedUnit(details.sizeUnit)}`,
                    }]
                  : []),
                ...(details.loadingMeters
                  ? [{
                      label: t('screens.cs.trip_details.shipment_details.loading_meters'),
                      value: `${formatDecimals(details.loadingMeters)} ${getFormattedUnit(details.loadingMetersUnit)}`,
                    }]
                  : []),
              ]}
            />
          ))}
        </KNDetailsPopover>
      )}

      {leg.attributes.dangerousGoodsDetails.length > 0 && (
        <KNDetailsPopover
          open={dangerousDetailsOpen}
          onClose={handleDangerousDetailsClose}
          anchorEl={dangerousDetailsAnchorEl}
        >
          {leg.attributes.dangerousGoodsDetails.map((legDetails) => (
            <KNDetailsPopoverList
              key={legDetails.description}
              description={legDetails.description}
              items={[
                {
                  label: t('screens.cs.trip_details.dangerous_goods.packages'),
                  value: legDetails.packagesType
                    ? `${legDetails.numberOfPackages} × ${legDetails.packagesType}`
                    : legDetails.numberOfPackages,
                },
                {
                  label: t('screens.cs.trip_details.dangerous_goods.un_number'),
                  value: legDetails.unNumber,
                },
                {
                  label: t('screens.cs.trip_details.dangerous_goods.class'),
                  value: legDetails.classType,
                },
                {
                  label: t('screens.cs.trip_details.dangerous_goods.packing_group'),
                  value: legDetails.packingGroup,
                },
                {
                  label: t('screens.cs.trip_details.dangerous_goods.quantity'),
                  value: legDetails.quantityType ? `${legDetails.quantity} × ${legDetails.quantityType}` : legDetails.quantity,
                },
              ]}
            />
          ))}
        </KNDetailsPopover>
      )}
    </>
  )
}

export default LegHeader
